import React, {MouseEvent, useCallback, useEffect, useRef, useState} from 'react';
import cx from 'classnames';
import {observer} from 'mobx-react';
import {noop} from '@priority-software/priority-style-react';
import {formsStore} from '../../../stores/Form/FormsStore';
import {Form as FormType} from '../../../stores/Form';
import {Grid} from './Grid';
import {listTypes} from './SelectListModal/enum';
import {FormToolbar} from './FormToolbar';
import {FormView} from './FormView';
import {codes, keys} from '../../../constants/KeyCodes';
import {closeFormBySession, startRecordDelete} from '../actions/form';
import {eventPrevented} from '../../../utils/eventPrevented';
import {chooseCloseZoomForm} from '../actions/zoom';
import {runSiblingSubform} from '../utils/grid-methods';
import {subFormNavDirections} from '../consts/grid';
import {clearFormData, clearOneRow, runFiltersFromForm, runSearchMode} from '../actions/filters';
import {executeSearch} from '../actions/searches';
import {CollapsedFormView} from './CollapsedFormView';
import {FormViewEnum} from '../../../stores/Form/Form';
import {formatEventForMacro} from '../../Macro/utils/macroEventFormats';
import {showFormHelp} from '../../../_shared/Help/actions/helpActions';
import {HelpBook} from '../../HelpBook';
import {isActiveForm} from '../utils/activeForm';
import {cx_menu_input_anchor} from './FormInput/constants';
import {useContextMenu} from '../../../hooks/contextMenu/useContextMenu';
import {showFormFieldHelp} from '../utils/help';
import {FilledFieldContextMenu} from '../consts/formContextMenu';
import {pasteToColumn, isExcelClipboardData, pasteToCellF10} from '../utils/pasteToColumn';
import {useF9Dialog} from '../../../hooks/F9Dialog/useF9Dialog';
import {reorganizeRecordsAfterDelete} from '../actions/delete';
import styles from '../styles/form.module.scss';
import {FormDesignPanel} from '../../FormDesign/components/FormDesignPanel';
import {createDataTestId} from '../../../utils/dataTestId';

type Props = {
    currentForm: FormType;
    isApiLock: () => boolean;
    isSubForm?: boolean;
    openSelectListModal: (listType: listTypes) => void;
    componentClasses?: string;
    openNewEmptyRecord?: () => void;
    hideNewEmptyRecord?: () => void;
};

export const Form = observer(({componentClasses, openNewEmptyRecord, ...restProps}: Props) => {
    const {currentForm, isSubForm, isApiLock: isLock, hideNewEmptyRecord} = restProps;
    const anchorElement = useRef<HTMLElement>(null);
    const tableWrapper = useRef<HTMLDivElement>(null);
    const isActive = isActiveForm(currentForm);
    const currentRecord = currentForm.records[currentForm.currentRow];
    const activeKeys = currentRecord?.keys;
    const viewType = currentForm.view;
    const isApiLock = isLock();
    const callHelpOnField = () => {
        const {currentColumn} = formsStore.getActiveForm();
        showFormFieldHelp({anchor: anchorElement.current, column: currentColumn});
        closeContextMenu();
    };

    const EditModeFunc = () => {
        const {tableAPI, currentColumn} = formsStore.currentForm;
        currentColumn.setEditMode(true);
        currentColumn.setMaxWidthMode(true);
        tableAPI.recalcColumnStyles();
        closeContextMenu();
    };

    const openFormDesignPanel = () => {
        setIsDesignOpen(true);
        closeContextMenu();
    };

    const renderItem = FilledFieldContextMenu({
        helpFieldFunc: callHelpOnField,
        moveEditFunc: EditModeFunc,
        openFormDesignFunc: openFormDesignPanel,
        clearAllFunc: clearFormData,
        hideRecordFunc: clearOneRow
    });

    const {openContextMenu, closeContextMenu, contextMenuComponent} = useContextMenu({
        menuData: renderItem
    });

    const {openF9Dialog, F9Dialog, isOpen} = useF9Dialog({});
    const [isDesignOpen, setIsDesignOpen] = useState(false);

    useEffect(
        function handleShortCodes() {
            if (isActive) {
                if (currentForm.collapsed) {
                    // Expand form view when the form becomes active again, i.e. after clicking ESC from child form

                    currentForm.toggleViewState(false);
                }

                const processEventCodes = async (event: KeyboardEvent) => {
                    if (event.ctrlKey && event.code === codes.B) {
                        event.preventDefault();
                        setIsDesignOpen(true);
                    }
                };

                const handleKeyDown = async e => {
                    formsStore.setEventForRecordingMacro(formatEventForMacro({e}));

                    if (e.repeat || isApiLock) return;

                    switch (e.key) {
                        case keys.F1: {
                            if (e.ctrlKey || e.metaKey) {
                                showFormHelp();
                            }
                            break;
                        }
                        case keys.F7: {
                            if (!formsStore.currentScreen.isRecordDialog) {
                                if (e.ctrlKey || e.metaKey) {
                                    await clearFormData(currentForm);
                                    break;
                                }
                                await clearOneRow(currentForm);
                            }
                            break;
                        }
                        case keys.F9: {
                            if (e.ctrlKey || e.metaKey) {
                                openF9Dialog(true);
                            } else {
                                openF9Dialog(false);
                            }
                            break;
                        }
                        case keys.F10: {
                            pasteToCellF10(currentForm);
                            break;
                        }
                        case keys.ESC:
                            if (isSubForm) {
                                await formsStore.currentScreen.handleFormLevel(
                                    currentForm.parentForm?.level
                                );
                            } else {
                                const currentScreen = formsStore.currentScreen;

                                if (!currentScreen.isRecordDialog) {
                                    await closeFormBySession(
                                        currentForm.session,
                                        !currentScreen.zoomSource
                                    );
                                }
                            }
                            break;

                        case keys.ENTER:
                            e.stopPropagation();
                            await runFiltersFromForm(currentForm, currentForm.searchModeLevel);
                            break;

                        case keys.F12:
                            if (activeKeys) {
                                // Go to subform
                                eventPrevented(e, true);
                                const firstSubForm = currentForm.subFormsList?.[0];
                                const subFormName =
                                    currentForm.activeSubForm?.ename || firstSubForm?.Ename;
                                const isHTMLForm = currentForm.activeSubForm
                                    ? currentForm.activeSubForm.form?.Ishtml
                                    : firstSubForm?.IsText;

                                const currentScreen = formsStore.currentScreen;

                                if (subFormName) {
                                    await currentScreen.runSubForm(
                                        subFormName,
                                        currentForm,
                                        isHTMLForm
                                    );
                                }
                            }
                            break;

                        case keys.F11:
                            eventPrevented(e, true);
                            if (!formsStore.currentScreen.isRecordDialog) {
                                if (e.shiftKey) {
                                    currentForm.searchStore.openSearch();
                                    break;
                                }
                                runSearchMode(currentForm, currentForm.searchModeLevel);
                            }
                            break;

                        case keys.F8:
                            const currentScreen = formsStore.currentScreen;

                            if (currentScreen.zoomSource) {
                                await chooseCloseZoomForm(currentScreen);
                            } else if (!isSubForm) {
                                await closeFormBySession(currentForm.session);
                            }
                            break;

                        case keys.F4:
                            const nextFormView =
                                currentForm.view === FormViewEnum.table
                                    ? FormViewEnum.form
                                    : FormViewEnum.table;
                            currentForm.toggleFormView(nextFormView);
                            break;

                        case keys.ARROW_RIGHT:
                            if (e.altKey && isSubForm) {
                                eventPrevented(e, true);
                                await runSiblingSubform(subFormNavDirections.right, currentForm);
                            }
                            break;

                        case keys.ARROW_LEFT:
                            if (e.altKey && isSubForm) {
                                eventPrevented(e, true);
                                await runSiblingSubform(subFormNavDirections.left, currentForm);
                            }
                            break;
                        case keys.DELETE:
                            if (e.ctrlKey) {
                                eventPrevented(e, true);
                                const row = currentForm.currentRecord;
                                if (
                                    row?.keys ||
                                    row?.getField(currentForm.currentColumnId)?.currentValue
                                ) {
                                    await startRecordDelete(row, async () => {
                                        if (!currentForm.parentForm) {
                                            hideNewEmptyRecord?.();
                                        }
                                        reorganizeRecordsAfterDelete(currentForm);
                                    });
                                }
                            }
                            break;
                        default:
                            break;
                    }
                    processEventCodes(e);
                    if (e.key >= '0' && e.key <= '9' && e.ctrlKey) {
                        e.preventDefault();
                        const searchToExecute = currentForm?.searchStore.savedSearches?.find(
                            search => search.index === Number(e.key)
                        );
                        if (searchToExecute) {
                            executeSearch(currentForm, searchToExecute);
                        }
                    }
                };

                const handleMouseClick = e => {
                    formsStore.setEventForRecordingMacro(formatEventForMacro({e}));
                };

                const onPaste = e => {
                    // Use native paste if clipboard doesn't include excel like characters
                    if (!isExcelClipboardData(e)) return;

                    // TODO: handle query mode case

                    e.preventDefault();
                    pasteToColumn(e);
                };
                const copyRef = tableWrapper.current;
                window.addEventListener('keydown', handleKeyDown);
                window.addEventListener('click', handleMouseClick);
                copyRef.addEventListener('paste', onPaste);
                return () => {
                    window.removeEventListener('keydown', handleKeyDown);
                    window.removeEventListener('click', handleMouseClick);
                    copyRef.removeEventListener('paste', onPaste);
                };
            }
            return noop;
        },
        [isActive, isSubForm, isApiLock, activeKeys, currentForm, hideNewEmptyRecord]
    );

    const viewRender = () => {
        if (currentForm.collapsed) {
            return <CollapsedFormView currentForm={currentForm} />;
        } else if (viewType === FormViewEnum.table && !formsStore.currentScreen.isRecordDialog) {
            return (
                <Grid
                    {...restProps}
                    isActiveForm={isActive}
                    defaultHeight={currentForm.tableHeight}
                />
            );
        }
        return <FormView {...restProps} isActiveForm={isActive} />;
    };

    const handleContext = async (e: MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
        e.preventDefault();
        if (isApiLock) {
            return;
        }
        const target = e.target as HTMLDivElement;
        const anchor: HTMLElement = target.closest(`.${cx_menu_input_anchor}`);

        if (isActive) {
            if (anchor?.dataset?.name) {
                const column = currentForm.getColumnByName(anchor.dataset.name);
                currentForm.setCurrentRow(Number(anchor.dataset.rowidx));
                currentForm.setCurrentColumnId(column.id);
                anchorElement.current = anchor;
                openContextMenu(anchor, e.clientX, e.clientY);
            }
        } else {
            const {currentScreen} = formsStore;
            await currentScreen.handleFormLevel(currentForm.level);
        }
    };

    const {title} = currentForm;

    const {
        helpPageQueue,
        isLoadingHelpData,
        addHelpPage,
        setHelpPageQuery,
        setHelpActiveListPage,
        removeHelpPage,
        formHelp,
        showHelp
    } = currentForm.helpBook;

    const onClose = useCallback(() => {
        currentForm.helpBook.setShowHelp(false);
        currentForm.helpBook.initHelpPageQueue();
    }, [currentForm.helpBook]);

    return (
        <>
            {!currentForm.collapsed ? (
                <div
                    className={cx(componentClasses)}
                    {...createDataTestId('form-collapsed-container', false)}
                >
                    <FormToolbar
                        isFormActive={isActive}
                        openRecordDialog={openNewEmptyRecord}
                        currentForm={restProps.currentForm}
                    />
                </div>
            ) : null}

            <div
                {...createDataTestId('form-container', false)}
                ref={tableWrapper}
                className={cx(styles.component, componentClasses)}
                onContextMenu={handleContext}
                onKeyDown={closeContextMenu}
            >
                {viewRender()}
                {contextMenuComponent}
                {isOpen && F9Dialog}
                <HelpBook
                    helpPageQueue={helpPageQueue}
                    onClose={onClose}
                    title={title}
                    isLoadingHelpData={isLoadingHelpData}
                    addHelpPage={addHelpPage}
                    setHelpPageQuery={setHelpPageQuery}
                    setHelpActiveListPage={setHelpActiveListPage}
                    removeHelpPage={removeHelpPage}
                    formHelp={formHelp}
                    showHelp={showHelp}
                />
                {isDesignOpen && <FormDesignPanel onClose={() => setIsDesignOpen(false)} />}
            </div>
        </>
    );
});
